"use client";

import { create } from "zustand";
import type { BuilderPolishPayment } from "@/lib/cv-builder/polish-client";

type CvBuilderPolishPhase =
  | "intro"
  | "preparing_payment"
  | "payment"
  | "generating"
  | "success";

/**
 * Where the dialog was opened from. Only `upgrade_modal` has already pitched
 * the product and shown a price, so only that source may skip the intro and
 * jump straight to a QR code.
 */
export type CvBuilderPolishSource = "toolbar" | "upgrade_modal";

interface CvBuilderPolishState {
  isOpen: boolean;
  phase: CvBuilderPolishPhase;
  source: CvBuilderPolishSource;
  payment: BuilderPolishPayment | null;
  error: string | null;
  openDialog: (source: CvBuilderPolishSource) => void;
  closeDialog: () => void;
  setPreparingPayment: () => void;
  setPayment: (payment: BuilderPolishPayment) => void;
  setGenerating: () => void;
  setSuccess: () => void;
  setError: (error: string) => void;
  resetPayment: () => void;
}

const initialState = {
  isOpen: false,
  phase: "intro" as const,
  source: "toolbar" as const,
  payment: null,
  error: null,
};

export const useCvBuilderPolishStore = create<CvBuilderPolishState>((set) => ({
  ...initialState,
  openDialog: (source) =>
    set({ isOpen: true, source, phase: "intro", error: null }),
  closeDialog: () => set({ ...initialState }),
  setPreparingPayment: () => set({ phase: "preparing_payment", error: null }),
  setPayment: (payment) =>
    set({ isOpen: true, phase: "payment", payment, error: null }),
  setGenerating: () => set({ phase: "generating", error: null }),
  setSuccess: () => set({ phase: "success", payment: null, error: null }),
  setError: (error) => set({ error }),
  resetPayment: () => set({ phase: "intro", payment: null, error: null }),
}));
